import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Search, Tag, Calendar, User, Clock, PlusCircle, Edit2, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Breadcrumbs from './Navigation/Breadcrumbs';
import { supabase } from '../lib/supabaseClient';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
}

function BlogPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_statia')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPosts(data || []);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту статью?')) return;

    try {
      const { error } = await supabase
        .from('blog_statia')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPosts(posts.filter(post => post.id !== id));
    } catch (error: any) {
      console.error('Error deleting post:', error);
    }
  };

  const categories = [
    { name: 'Все', count: 12 },
    { name: 'Бизнес', count: 4 },
    { name: 'Логистика', count: 3 },
    { name: 'Таможня', count: 2 },
    { name: 'Аналитика', count: 3 }
  ];

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || selectedCategory === 'Все' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Блог о доставке и экспедировании товаров из Китая | KiTLiN</title>
        <meta name="description" content="Полезные статьи о бизнесе с Китаем, логистике, таможенном оформлении и трендах рынка. Экспертные советы по работе с китайскими поставщиками." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://kitlin.ru/blog" />
        <meta property="og:title" content="Блог о доставке и экспедировании товаров из Китая | KiTLiN" />
        <meta property="og:description" content="Полезные статьи о бизнесе с Китаем, логистике, таможенном оформлении и трендах рынка. Экспертные советы по работе с китайскими поставщиками." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=1200&h=630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://kitlin.ru/blog" />
        <meta name="twitter:title" content="Блог о доставке и экспедировании товаров из Китая | KiTLiN" />
        <meta name="twitter:description" content="Полезные статьи о бизнесе с Китаем, логистике, таможенном оформлении и трендах рынка." />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=1200&h=630" />
        
        {/* Structured Data for Blog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "headline": "Блог о доставке и экспедировании товаров из Китая",
            "description": "Полезные статьи о бизнесе с Китаем, логистике, таможенном оформлении и трендах рынка.",
            "publisher": {
              "@type": "Organization",
              "name": "KiTLiN",
              "logo": {
                "@type": "ImageObject",
                "url": "https://i.ibb.co/nj9SZYb/Kitlin-3.png"
              }
            },
            "blogPost": posts.map(post => ({
              "@type": "BlogPosting",
              "headline": post.title,
              "description": post.excerpt,
              "image": post.image,
              "author": {
                "@type": "Person",
                "name": post.author
              },
              "datePublished": "2024-03-15T10:00:00+03:00",
              "url": `https://kitlin.ru/blog/${post.id}`
            }))
          })}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="pt-20 pb-24 px-6 bg-gradient-to-b from-[#fbfbfd] to-white">
        <div className="max-w-[980px] mx-auto">
          <div className="mb-8">
            <Breadcrumbs />
          </div>
          <h1 className="text-5xl md:text-6xl font-semibold text-[#1d1d1f] mb-6 tracking-tight">
            Блог
          </h1>
          <p className="text-xl md:text-2xl text-[#86868b] max-w-3xl mx-auto leading-relaxed">
            Полезные статьи о бизнесе с Китаем, логистике и трендах рынка
          </p>
        </div>
      </section>

      {/* Search and Categories */}
      <section className="py-12 px-6 bg-[#f5f5f7]">
        <div className="max-w-[980px] mx-auto">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Search */}
            <div className="flex-1">
              {isAdmin && (
                <Link
                  to="/blog/new"
                  className="inline-flex items-center bg-[#0171E3] text-white px-6 py-3 rounded-full hover:bg-[#0156aa] transition-all font-medium mb-4"
                >
                  <PlusCircle className="w-5 h-5 mr-2" />
                  Новая статья
                </Link>
              )}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Поиск статей..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-12 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>
            </div>
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category.name
                      ? 'bg-[#0171E3] text-white'
                      : 'bg-white text-[#1d1d1f] hover:bg-gray-100'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-24 px-6">
        <div className="max-w-[980px] mx-auto">
          <div className="grid grid-cols-1 gap-12">
            {filteredPosts.map((post) => (
              <article key={post.id} className="flex flex-col md:flex-row gap-8 group">
                {/* Image */}
                <Link 
                  to={`/blog/${post.id}`}
                  className="md:w-1/3 relative overflow-hidden rounded-2xl block"
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-64 md:h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                </Link>
                {/* Content */}
                <div className="md:w-2/3">
                  {/* Mobile-optimized metadata */}
                  <div className="flex flex-wrap gap-4 text-sm text-[#86868b] mb-4">
                    <span className="inline-flex items-center">
                      <Tag className="w-4 h-4 mr-1" />
                      {post.category}
                    </span>
                    <span className="inline-flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {post.date}
                    </span>
                    <span className="inline-flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {post.author}
                    </span>
                    <span className="inline-flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {post.readTime}
                    </span>
                  </div>
                  <Link to={`/blog/${post.id}`}>
                    <h2 className="text-2xl font-semibold text-[#1d1d1f] mb-4 group-hover:text-[#0171E3] transition-colors">{post.title}</h2>
                  </Link>
                  {isAdmin && (
                    <div className="flex gap-2 mb-4">
                      <Link
                        to={`/blog/edit/${post.id}`}
                        className="p-2 text-gray-500 hover:text-[#0171E3] rounded-full hover:bg-gray-100"
                      >
                        <Edit2 className="w-5 h-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="p-2 text-gray-500 hover:text-[#E20411] rounded-full hover:bg-gray-100"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                  <p className="text-[#86868b] mb-6 line-clamp-3">{post.excerpt}</p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-[#f5f5f7] text-[#1C1F7E] rounded-full text-sm"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="inline-flex items-center text-[#1C1F7E] hover:text-[#E20411] transition-colors group/link"
                    >
                      Читать далее
                      <ChevronRight className="w-5 h-5 ml-1 transform group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default BlogPage;